import { TMChangeOrderTypeAPI } from '@business/api/tm_change_order_type';
import { TMChangeOrderTypeId, TMChangeOrderTypeRequest, TMChangeOrderTypeResponse } from '@business/entities/tm';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

/**
 * Change order types (Customer, Subcontractor, Administrative Charge, Internal...). Admins manage them in Settings;
 * the change order form and the permission editor list them.
 */
export class TMChangeOrderTypeBloc extends BaseBloc {
  private readonly repo = new Repository<TMChangeOrderTypeResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.repo.items$;
  readonly selectItem = this.repo.selectItem;

  onReset() {
    this.repo.reset();
  }

  fetchChangeOrderTypes = () => {
    return TMChangeOrderTypeAPI.fetchChangeOrderTypes().pipe(
      this.repo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_change_order_types_error') }),
    );
  };

  createChangeOrderType = (data: TMChangeOrderTypeRequest) => {
    return TMChangeOrderTypeAPI.createChangeOrderType(data).pipe(
      this.repo.ops.addOne(item => item),
      handleMessage({ type: requestMessage('create_change_order_type') }),
    );
  };

  updateChangeOrderType = (id: TMChangeOrderTypeId, data: TMChangeOrderTypeRequest) => {
    return TMChangeOrderTypeAPI.updateChangeOrderType(id, data).pipe(
      this.repo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('update_change_order_type') }),
    );
  };

  deleteChangeOrderType = (id: TMChangeOrderTypeId) => {
    return showDeleteConfirmation('Delete Change Order Type', 'Do you really want to remove this change order type?').pipe(
      switchMap(() => TMChangeOrderTypeAPI.deleteChangeOrderType(id)),
      this.repo.ops.removeOne(() => id),
      handleMessage({ type: requestMessage('delete_change_order_type') }),
    );
  };
}
